import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../application/router/routes.ts';
import Loader from '../../common/components/Loader.tsx';
import { axiosInstance } from '../../common/axiosInstans.ts';
import {
    TextField,
    Select,
    MenuItem,
    Button,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    InputAdornment,
    Paper,
    Card,
    CardContent,
    Typography,
    TablePagination,
    Tooltip,
    IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import RestoreIcon from '@mui/icons-material/Restore';
import EmptyTablePlaceholder from '../../common/components/EmptyTablePlaceholder.tsx';
import { Drug } from '../../application/types/drug.ts';
import { useGlobalStore } from '../../application/store/store.tsx';
import ConfirmDialog from './components/ConfirmDialog.tsx';

const DrugManagement = () => {
    const [drugs, setDrugs] = useState<Drug[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filter, setFilter] = useState('active');
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const setMessage = useGlobalStore((state) => state.setMessage);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Reactivation dialog state
    const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
    const [drugToReactivate, setDrugToReactivate] = useState<Drug | null>(null);
    const [isReactivating, setIsReactivating] = useState(false);

    useEffect(() => {
        fetchDrugs();
    }, [filter]);

    const fetchDrugs = async () => {
        setIsLoading(true);
        try {
            let response;
            if (filter === 'inactive') {
                response = await axiosInstance.get('/drug/inactive');
            } else {
                response = await axiosInstance.get('/drug');
            }
            setDrugs(response.data);
        } catch (error) {
            console.error('Error fetching drugs:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReactivateClick = (drug: Drug, event: React.MouseEvent) => {
        event.stopPropagation();
        setDrugToReactivate(drug);
        setReactivateDialogOpen(true);
    };

    const reactivateDrug = async () => {
        if (!drugToReactivate) return;

        try {
            setIsReactivating(true);
            await axiosInstance.put(`/drug/reactivate/${drugToReactivate.id}`);
            setDrugs((prevDrugs) =>
                prevDrugs.filter((d) => d.id !== drugToReactivate.id)
            );
            setMessage('Läkemedel återaktiverat', 'success');
            setReactivateDialogOpen(false);
            setDrugToReactivate(null);
        } catch (error) {
            setMessage('Ett fel uppstod vid återaktivering', 'error');
            console.error('Error reactivating drug:', error);
        } finally {
            setIsReactivating(false);
        }
    };

    // Filtered drugs based on search input
    const filteredDrugs = drugs.filter(
        (drug) =>
            drug.drugName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            drug.atc?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Paginated data
    const paginatedData = filteredDrugs.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Handle pagination
    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Paper className="p-3">
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Hantera läkemedel
            </Typography>
            <Card className="mb-3 p-2">
                <CardContent className="flex gap-2 flex-wrap">
                    <TextField
                        placeholder="Sök läkemedel..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        className="w-[250px]"
                    />

                    <Select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        size="small"
                        className="min-w-[120px]"
                    >
                        <MenuItem value="active">Aktiva</MenuItem>
                        <MenuItem value="inactive">Inaktiva</MenuItem>
                    </Select>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(NavigationRoutes.createDrug)}
                    >
                        <AddIcon className="mr-2" /> Nytt läkemedel
                    </Button>
                </CardContent>
            </Card>

            <TableContainer component={Paper}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Läkemedel</TableCell>
                            <TableCell>Styrka</TableCell>
                            <TableCell>Mängd</TableCell>
                            <TableCell>Doseringsform</TableCell>
                            <TableCell>ATC</TableCell>
                            {filter === 'inactive' && (
                                <TableCell align="right">Åtgärd</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.length === 0 ? (
                            <EmptyTablePlaceholder
                                colSpan={filter === 'inactive' ? 6 : 5}
                                title="Inga läkemedel hittades"
                                subtitle="Justera filter/sökning eller klicka på 'Nytt läkemedel' för att lägga till."
                            />
                        ) : (
                            paginatedData.map((drug) => (
                                <TableRow
                                    key={drug.id}
                                    onClick={
                                        drug.isActive
                                            ? () =>
                                                  navigate(
                                                      `${NavigationRoutes.drugView}/${drug.id}`,
                                                      { state: { id: drug.id } }
                                                  )
                                            : null
                                    }
                                    className={`odd:bg-gray-100 ${
                                        drug.isActive
                                            ? 'hover:cursor-pointer'
                                            : 'opacity-60 cursor-not-allowed'
                                    }`}
                                >
                                    {/* Note: The drug list endpoint does not include synonyms array for performance reasons.
                                        Only allowSynonyms flag is available here. Full synonym data is fetched in DrugView. */}
                                    <TableCell>
                                        <span className="flex items-center gap-2">
                                            {drug.drugName}
                                            {drug.allowSynonyms && (
                                                <Tooltip title="Har synonymer">
                                                    <LinkIcon
                                                        fontSize="small"
                                                        color="info"
                                                    />
                                                </Tooltip>
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        {drug.concentration}{' '}
                                        {drug.concentrationUnit}
                                    </TableCell>
                                    <TableCell>
                                        {drug.quantity} {drug.quantityUnit}
                                    </TableCell>
                                    <TableCell>{drug.dosageForm}</TableCell>
                                    <TableCell>{drug.atc}</TableCell>
                                    {filter === 'inactive' && (
                                        <TableCell align="right">
                                            <Tooltip title="Återaktivera">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) =>
                                                        handleReactivateClick(
                                                            drug,
                                                            e
                                                        )
                                                    }
                                                    color="primary"
                                                >
                                                    <RestoreIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                {/* Pagination */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredDrugs.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Antal poster:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} av ${count !== -1 ? count : `mer än ${to}`}`
                    }
                />
            </TableContainer>

            {/* Reactivate Confirmation Dialog */}
            <ConfirmDialog
                open={reactivateDialogOpen}
                onClose={() => {
                    setReactivateDialogOpen(false);
                    setDrugToReactivate(null);
                }}
                onConfirm={reactivateDrug}
                title="Återaktivera läkemedel"
                message={`Är du säker på att du vill återaktivera "${drugToReactivate?.drugName}"?`}
                confirmText="Återaktivera"
                confirmingText="Återaktiverar..."
                confirmColor="success"
                isLoading={isReactivating}
            />
        </Paper>
    );
};

export default DrugManagement;
